import { Middleware } from 'redux';

import {
    FETCH_REQUEST,
    FetchFailureAction, FetchInitAction, FetchSuccessAction, TransformInitOptions
} from './fetch-middleware.types';
import {
    defaultResponseTransform, getFetchFailureActionType,
    getFetchLoadActionType, getFetchSuccessActionType, isFetchRequestAction,
    resolveErrorPayload
} from './utils';

export type FetchClient = (input: string, init?: RequestInit) => Promise<Response>;

type Options<TState = any> = {
    fetchClient?: FetchClient
    defaultTransformInit?: (options: TransformInitOptions<TState>) => RequestInit
};

/**
 * Abstraction on the browser fetch client api to use with Redux actions.
 *
 * @external https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API.
 *
 * To keep Portal Shell small and while it is simple,
 * why not use this api directly without any third party dependencies?
 *
 * Other alternatives could be used for more complex apps:
 * eg https://github.com/tannerlinsley/react-query or https://redux-toolkit.js.org/rtk-query/overview
 */
export function fetchMiddlewareCreator<TState> (options: Options = {}): Middleware<Record<string, unknown>, TState> {
    return ({
        dispatch, getState
    }) => (next) => (action) => {
        if (!isFetchRequestAction(action)) {
            return next(action);
        }

        const {
            name, ...restInit
        } = action.payload;
        const transformResponse = action.payload.transformResponse || defaultResponseTransform;
        const transformInit = action.payload.transformInit || options.defaultTransformInit || null;

        if (typeof name !== 'string') {
            throw new Error(`Expected a fetch name on ${FETCH_REQUEST} payload`);
        }

        let input: null | string = null;

        if ('url' in action.payload) {
            input = action.payload.url;
        }

        if ('resolveUrl' in action.payload) {
            input = action.payload.resolveUrl(getState());
        }

        if (!input) {
            throw new Error(`Expected url or resolveUrl on ${FETCH_REQUEST} payload`);
        }

        dispatch<FetchInitAction>({ type: getFetchLoadActionType(name) });

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...action.payload?.headers as any || {}
        };
        const state = getState() as any;

        let init: RequestInit = {
            headers,
            ...restInit
        };

        if (transformInit) {
            try {
                init = transformInit({
                    state,
                    init,
                    dispatch
                });
            } catch (error) {
                return onFailure(error);
            }
        }

        if (options.fetchClient) {
            return options.fetchClient(input, init).then(onResolved).catch(onFailure);
        } else {
            return fetch(input, init).then(onResolved).catch(onFailure);
        }

        async function onResolved (response: Response) {
            if (!response.ok) {
                return onFailure(response);
            }

            try {
                const data = await transformResponse(response);

                dispatch<FetchSuccessAction>({
                    type: getFetchSuccessActionType(name),
                    meta: { response },
                    payload: { data }
                });

                return data;
            } catch (error) {
                console.error('Unable to transformResponse, check your fetch transformResponse options');

                return null;
            }
        }

        async function onFailure (error: any) {
            const errorPayload = await resolveErrorPayload(error);

            dispatch<FetchFailureAction>({
                type: getFetchFailureActionType(name),
                payload: { error: errorPayload },
                meta: { init }
            });

            return errorPayload;
        }
    };
}
